import {Component} from "@angular/core";
import {ComponentBase} from "../base/ComponentBase";
import { Directive, ElementRef, Input } from '@angular/core';
import {EventBus} from "../event/EventBus";
import {StepStatusEvent} from "../event/StepEvents";
import {RaVersion} from "../services/configuration/ra.version";
import {CompletionState} from "../services/usermodel/usermodel.objects";
import {filter} from "rxjs/operators";

@Component({
    selector: "step-circle",
    templateUrl: "./step-circle.component.html",
    styleUrls: ["./step-circle.component.scss"]
})

export class StepCircleComponent extends ComponentBase {

    @Input("progress")
    set progress(value: number) {
        this._progress = value > 100 ? 100 : value;
    };
    @Input("complete")
    set complete(value: CompletionState) {
        if (value === CompletionState.COMPLETED) {
            this._complete = true;
        }
    }
    @Input("entries")
    set entries(value: any) {
        this.setupDefaultNumberOfArcs(value);
    }
    @Input() stepActive: boolean;
    @Input() stepAllowed: boolean;
    @Input() smaller: boolean = false;

    public numberOfArcs: any;
    private _progress: number;
    private _complete: boolean = false;
    private zeroArcDefault: string = "linear-gradient(to top, transparent 0%, rgb(222,222,222) 0%), linear-gradient(270deg, transparent 50%, rgb(240, 125, 236) 50%)";
    private below180Arc: string = "linear-gradient(VALUEdeg, transparent 50%, rgb(222,222,222) 50%), linear-gradient(270deg, transparent 50%, rgb(240, 125, 236) 50%)";
    private above180Arc: string = "linear-gradient(270deg, transparent 50%, #f07dec 50%),linear-gradient(VALUEdeg, transparent 50%, #f07dec 50%)";
    private completeState: string = "linear-gradient(to top, transparent 0%, rgb(52, 219, 122) 0%), linear-gradient(270deg, transparent 50%, rgb(240, 125, 236) 50%)";
    private DEFAULT_FULL_DEGREE = 90;

    constructor(public el: ElementRef, private eventBus: EventBus) {
        super("StepCircleComponent");
        this.eventBus.pipe(filter(e => e instanceof StepStatusEvent)).subscribe((e: StepStatusEvent) => {
            this.setupDefaultNumberOfArcs(this.entries);
        });
    }

    private setupDefaultNumberOfArcs(entries: any): void {
        if (!!entries) {
            this.numberOfArcs = entries;
        } else {
            this.numberOfArcs = Array(3).fill(0).map((x, i) => i);
        }
    }

    private getRotationAmount(index: number): number {
        let rotate = (index * (360 / this.numberOfArcs.length)) + 270;
        return rotate >= 360 ? rotate - 360 : rotate;
    }

    private below180ArcDegree(percentage: number): number {
        if (percentage < 0.5) {
            return (percentage * 360) - 95;
        } else {
            return this.DEFAULT_FULL_DEGREE;
        }
    }

    private above180ArcDegree(percentage: number): number {
        if (percentage < 1) {
            return ((percentage - 0.5) * 360) - 83;
        } else {
            return this.DEFAULT_FULL_DEGREE;
        }
    }

    private determineProgressAngleDegree(progress: number, percentage: number): number {
        if (progress <= 50) {
            return this.below180ArcDegree(percentage);
        } else {
            return this.above180ArcDegree(percentage);
        }
    }

    private determineProgressTemplate(percentage: number, value: number): string {
        let template = this.completeState;

        if (percentage === 1 && this._complete) {
            return template;
        } else if (percentage === 1 && !this._complete) {
            template = this.above180Arc;
            return template.replace(new RegExp("VALUE"), (String)(value));
        }

        template = ((percentage * 360) <= 180) ? this.below180Arc : this.above180Arc;
        return template.replace(new RegExp("VALUE"), (String)(value));
    }

    public getCircleBackground(): any {
        let template = this.zeroArcDefault;
        let percentage = this._progress / 100;

        if (this._progress > 0) {
            let value = this.determineProgressAngleDegree(this._progress, percentage);
            template = this.determineProgressTemplate(percentage, value);
        }

        return { "background-image": template };
    }

    public getArcDimensions(index: number): any {
        let rotate = this.getRotationAmount(index);
        let skew = -30;

        if (this.numberOfArcs.length === 1) {
            return { "border": "none" };
        } else if (this.numberOfArcs.length === 2) {
            return this.handleTwoArcDimensions(index);
        } else if (this.numberOfArcs.length > 3 && this.numberOfArcs.length < 7) {
            skew = (this.numberOfArcs.length - 4) * 15;
        } else if (this.numberOfArcs.length >= 7 && this.numberOfArcs.length !== 9) {
            skew = 38 + ((this.numberOfArcs.length - 7) * 5);
        } else if (this.numberOfArcs.length === 9) {
            skew = 50;
        }

        return {
            "transform": "rotate(" + rotate + "deg) skew(" + skew + "deg)",
            "animation-delay": index * 0.1 + "s"
        };
    }

    public getCircleBorderSelectors(): any {
        let cssClasses = {};

        if (this.stepActive) {
            cssClasses["active-border"] = true;
        } else {
            cssClasses["active-border-minimum"] = true;
        }

        return cssClasses;
    }

    public getCircleBorderDisallowed(): any {
        return this.stepAllowed ? {} : { "active-border-disallowed": true };
    }

    public getCircleActiveState(): any {
        let cssClasses = { "smaller": this.smaller };

        if (this.stepActive) {
            cssClasses["step-circle"] = true;
        } else {
            cssClasses["step-circle-minimum"] = true;
        }

        return cssClasses;
    }

    public handleTwoArcDimensions(index: number): any {
        let rotate = this.getRotationAmount(index);
        let skew = (this.numberOfArcs.length - 4) * 15;
        let bottom = (index === 0) ? "7em" : "0em";

        return {
            "transform": "rotate(" + rotate + "deg) skew(" + skew + "deg)",
            "width": "8em",
            "bottom": bottom
        };
    }

}
